import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams, NavLink, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import type { Project } from "../../../repositories/projectRepository";
import type { Build } from "../../../repositories/buildRepository";
import type { Storefront } from "../../../repositories/storefrontRepository";
import type { DeployJob } from "../../../repositories/deployJobRepository";

export interface ProjectContextData {
  project: Project;
  builds: Build[];
  storefronts: Storefront[];
  deployJobs: DeployJob[];
  setBuilds: React.Dispatch<React.SetStateAction<Build[]>>;
  setStorefronts: React.Dispatch<React.SetStateAction<Storefront[]>>;
  setProject: React.Dispatch<React.SetStateAction<Project | null>>;
  setDeployJobs: React.Dispatch<React.SetStateAction<DeployJob[]>>;
}

export function ProjectLayout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [builds, setBuilds] = useState<Build[]>([]);
  const [storefronts, setStorefronts] = useState<Storefront[]>([]);
  const [deployJobs, setDeployJobs] = useState<DeployJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!id) return;

      try {
        const [projectRes, buildsRes, storefrontsRes, deployJobsRes] = await Promise.all([
          fetch(`/api/projects/${id}`, { credentials: "include" }),
          fetch(`/api/projects/${id}/builds`, { credentials: "include" }),
          fetch(`/api/projects/${id}/storefronts`, { credentials: "include" }),
          fetch(`/api/projects/${id}/deploy-jobs`, { credentials: "include" }),
        ]);

        if (!projectRes.ok) {
          if (projectRes.status === 404) {
            navigate("/projects");
            return;
          }
          throw new Error("Failed to fetch project");
        }

        const { data: projectData } = await projectRes.json();
        setProject(projectData);

        if (buildsRes.ok) {
          const { data: buildsData } = await buildsRes.json();
          setBuilds(buildsData);
        }

        if (storefrontsRes.ok) {
          const { data: storefrontsData } = await storefrontsRes.json();
          setStorefronts(storefrontsData);
        }

        if (deployJobsRes.ok) {
          const { data: deployJobsData } = await deployJobsRes.json();
          setDeployJobs(deployJobsData);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id, navigate]);

  // Poll for builds and deploy jobs updates every 5 seconds
  useEffect(() => {
    if (!id || loading) return;

    async function pollData() {
      try {
        const [buildsRes, deployJobsRes] = await Promise.all([
          fetch(`/api/projects/${id}/builds`, { credentials: "include" }),
          fetch(`/api/projects/${id}/deploy-jobs`, { credentials: "include" }),
        ]);

        if (buildsRes.ok) {
          const { data: buildsData } = await buildsRes.json();
          setBuilds(buildsData);
        }

        if (deployJobsRes.ok) {
          const { data: deployJobsData } = await deployJobsRes.json();
          setDeployJobs(deployJobsData);
        }
      } catch {
        // Silently ignore polling errors to avoid spamming the UI
      }
    }

    const interval = setInterval(pollData, 5000);
    return () => clearInterval(interval);
  }, [id, loading]);

  const handleTriggerBuild = async () => {
    if (!id) return;
    setTriggering(true);
    setError(null);

    try {
      const response = await fetch(`/api/projects/${id}/builds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Failed to trigger build");
      }

      const { data: newBuilds } = await response.json();
      setBuilds((prev) => [...newBuilds, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto w-full px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading project...</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-6xl mx-auto w-full px-6 py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Project not found</h2>
          <Button onClick={() => navigate("/projects")}>Back to Projects</Button>
        </div>
      </div>
    );
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `pb-3 px-1 border-b-[3px] transition-colors flex items-center gap-2 text-sm font-medium ${
      isActive
        ? "border-primary text-primary font-bold"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="max-w-6xl mx-auto w-full px-6 py-8">
      {/* Breadcrumb and Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-col gap-2">
          {/* Breadcrumbs */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Link to="/projects" className="hover:text-primary transition-colors flex items-center gap-1">
              <span className="material-symbols-outlined text-[18px]">folder_open</span>
              Projects
            </Link>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            <span className="font-medium text-foreground">{project.name}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{project.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/projects")} className="shadow-sm">
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            Back
          </Button>
          <Button onClick={handleTriggerBuild} disabled={triggering} className="shadow-lg shadow-primary/20">
            <span className="material-symbols-outlined text-[20px]">play_arrow</span>
            {triggering ? "Triggering..." : "Trigger Build"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-8">
        <nav className="flex gap-8">
          <NavLink to={`/projects/${id}/builds`} className={navLinkClass}>
            <span className="material-symbols-outlined text-[20px]">history</span>
            Builds
          </NavLink>
          <NavLink to={`/projects/${id}/settings`} className={navLinkClass}>
            <span className="material-symbols-outlined text-[20px]">settings</span>
            Settings
          </NavLink>
          <NavLink to={`/projects/${id}/storefronts`} className={navLinkClass}>
            <span className="material-symbols-outlined text-[20px]">storefront</span>
            Storefronts
          </NavLink>
        </nav>
      </div>

      <Outlet context={{ project, builds, storefronts, deployJobs, setBuilds, setStorefronts, setProject, setDeployJobs } satisfies ProjectContextData} />
    </div>
  );
}
